import { useAtom } from "jotai";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { phoneNumberAtom } from "@/utils/atoms";

import { TwoFactorConfirmationInput } from "./TwoFactorConfirmationInput";

export const AuthenticatorSmsConfirmForm = ({
  resendAction,
}: {
  resendAction: () => void;
}) => {
  const { t } = useTranslation();
  const [phoneNumber] = useAtom(phoneNumberAtom);

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold text-secondary-100">
        {t("portal:authenticator.sms.verification.title")}
      </h1>
      <p>
        {t("portal:authenticator.sms.verification.description", {
          number: phoneNumber,
        })}
      </p>
      <TwoFactorSMSValidation resendAction={resendAction} />
    </div>
  );
};

export const TwoFactorSMSValidation = ({
  resendAction,
}: {
  resendAction: () => void;
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(60);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setCountdown(60);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;

          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    startCountdown();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startCountdown]);

  const handleClick = useMemo(
    () =>
      debounce(
        () => {
          startCountdown();
          resendAction();
        },
        1000,
        { leading: true, trailing: false },
      ),
    [startCountdown, resendAction],
  );

  useEffect(() => {
    return () => {
      handleClick.cancel();
    };
  }, [handleClick]);

  return (
    <div className="flex w-fit flex-col gap-2">
      <TwoFactorConfirmationInput />
      <button
        type="button"
        onClick={handleClick}
        disabled={countdown > 0}
        className="w-fit font-semibold text-highlight-100 underline disabled:opacity-50"
      >
        {t("portal:authenticator.sms.verification.resend")}
        {countdown > 0 &&
          ` ${t("portal:authenticator.sms.verification.secondsLeft", { count: countdown })}`}
      </button>
    </div>
  );
};
